import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { publicAxios } from '@/lib/api/publicClient';
import { useAuthStore } from '@/lib/auth/authStore';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';

interface PublicBrandPageData {
  gated: boolean;
  name: string;
  slug: string;
  brand: {
    id: number;
    logo: string | null;
    backgroundImage: string | null;
    campaignName: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    accentColor: string | null;
    backgroundColor: string | null;
    fontColor: string | null;
    font: string | null;
  } | null;
}

/**
 * CH-03 — /b/:slug · la página pública de una marca.
 *
 * Vive en una URL estable para que los QR impresos hoy resuelvan cuando se
 * abra. Mientras la marca no la abra, sólo su equipo (y su agencia) la ve
 * completa; el resto recibe «aún no disponible» con un camino a iniciar
 * sesión, nunca una página en blanco ni un 404. Pinta con el brand kit de
 * Brand Studio, así que la vista cerrada hace de vista previa real.
 *
 * `noindex` mientras esté cerrada: no queremos que un buscador la recoja
 * antes de tiempo.
 */
export function PublicBrandPage() {
  const t = useT();
  const { slug = '' } = useParams<{ slug: string }>();
  const token = useAuthStore((s) => s.token);

  const q = useQuery({
    queryKey: ['public-brand', slug, !!token],
    queryFn: async () => {
      const res = await publicAxios.get<PublicBrandPageData>(`/co-companies/public-page/${encodeURIComponent(slug)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      return res.data;
    },
    enabled: slug.length > 0,
    retry: false,
  });

  const gated = q.data?.gated ?? true;
  useEffect(() => {
    if (!gated) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, [gated]);
  useEffect(() => {
    if (q.data?.name) document.title = `${q.data.name} · Xotski`;
  }, [q.data?.name]);

  if (q.isLoading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-[var(--color-bg-canvas)] text-[var(--color-fg-muted)]">
        <p className="text-sm">{t('common.loading')}</p>
      </main>
    );
  }
  if (q.isError || !q.data) {
    return (
      <main className="grid min-h-dvh place-items-center bg-[var(--color-bg-canvas)] px-4 text-center">
        <div>
          <h1 className="text-xl font-bold text-[var(--color-fg-primary)]">{t('publicBrand.notFoundTitle')}</h1>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{t('publicBrand.notFoundBody')}</p>
        </div>
      </main>
    );
  }

  const data = q.data;
  if (data.gated || !data.brand) {
    const next = encodeURIComponent(`/b/${slug}`);
    return (
      <main className="grid min-h-dvh place-items-center bg-[var(--color-bg-canvas)] px-4" data-testid="public-brand-gated">
        <div className="w-full max-w-md rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-8 text-center shadow-[var(--shadow-elevated)]">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[var(--color-bg-elevated)] text-[var(--color-fg-muted)]">
            <Lock className="h-5 w-5" />
          </span>
          <p className="mt-4 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">{data.name}</p>
          <h1 className="mt-1 text-2xl font-bold text-[var(--color-fg-primary)]">{t('publicBrand.gatedTitle')}</h1>
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">{t('publicBrand.gatedBody')}</p>
          {token ? (
            <p className="mt-4 text-xs text-[var(--color-fg-subtle)]">{t('publicBrand.gatedSignedIn')}</p>
          ) : (
            <Link
              to={`/login?next=${next}`}
              className="mt-5 inline-flex h-10 items-center rounded-full bg-[var(--color-xk-accent)] px-5 text-sm font-bold text-[var(--color-fg-on-accent)]"
            >
              {t('publicBrand.signIn')}
            </Link>
          )}
        </div>
      </main>
    );
  }

  const b = data.brand;
  const bg = b.backgroundColor ?? b.primaryColor ?? '#0b0b0b';
  const fg = b.fontColor ?? '#ffffff';
  const accent = b.accentColor ?? b.secondaryColor ?? '#c8e530';
  return (
    <main
      className="min-h-dvh"
      data-testid="public-brand-open"
      style={{
        background: b.backgroundImage
          ? `linear-gradient(rgb(0 0 0 / 0.35), rgb(0 0 0 / 0.55)), url(${resolveImageUrl(b.backgroundImage)}) center / cover no-repeat, ${bg}`
          : `linear-gradient(160deg, ${bg}, ${b.secondaryColor ?? bg})`,
        color: fg,
        fontFamily: b.font ? `${b.font}, var(--font-sans)` : undefined,
      }}
    >
      <div className="mx-auto flex min-h-dvh w-full max-w-3xl flex-col items-center justify-center px-6 py-16 text-center">
        {b.logo ? (
          <img src={resolveImageUrl(b.logo)} alt={data.name} className="max-h-24 max-w-[240px] object-contain" />
        ) : (
          <span className="grid h-20 w-20 place-items-center rounded-2xl text-3xl font-black" style={{ background: accent, color: bg }}>
            {data.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <h1 className="mt-8 text-4xl font-black tracking-tight sm:text-5xl">{b.campaignName || data.name}</h1>
        {b.campaignName ? <p className="mt-2 text-sm uppercase tracking-widest opacity-80">{data.name}</p> : null}
        <p className="mt-6 max-w-lg text-base opacity-90">{t('publicBrand.openBody')}</p>
        <span className="mt-10 inline-flex h-12 items-center rounded-full px-6 text-sm font-bold" style={{ background: accent, color: bg }}>
          {t('publicBrand.cta')}
        </span>
        <p className="mt-16 text-[11px] uppercase tracking-widest opacity-60">{t('publicBrand.poweredBy')}</p>
      </div>
    </main>
  );
}
